import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { IdleProvider } from './context/IdleContext';
import { SettingsProvider } from './context/SettingsContext';
import IdleWarningModal from './components/common/IdleWarningModal';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const AnalyticsPage = lazy(() => import('./pages/dashboard/AnalyticsPage'));
const ProductsPage = lazy(() => import('./pages/products/ProductsPage'));
const AddProductPage = lazy(() => import('./pages/products/AddProductPage'));
const ViewProductPage = lazy(() => import('./pages/products/ViewProductPage'));
const StockPage = lazy(() => import('./pages/stock/StockPage'));
const AdjustStockPage = lazy(() => import('./pages/stock/AdjustStockPage'));
const WarehousesPage = lazy(() => import('./pages/warehouses/WarehousesPage'));
const WarehouseDetailPage = lazy(() => import('./pages/warehouses/WarehouseDetailPage'));
const ShowroomsPage = lazy(() => import('./pages/showrooms/ShowroomsPage'));
const ShowroomDetailPage = lazy(() => import('./pages/showrooms/ShowroomDetailPage'));
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage'));
const CreateInvoicePage = lazy(() => import('./pages/invoices/CreateInvoicePage'));
const InvoiceDetailPage = lazy(() => import('./pages/invoices/InvoiceDetailPage'));
const QuotationsPage = lazy(() => import('./pages/quotations/QuotationsPage'));
const CreateQuotationPage = lazy(() => import('./pages/quotations/CreateQuotationPage'));
const QuotationDetailPage = lazy(() => import('./pages/quotations/QuotationDetailPage'));
const SalariesPage = lazy(() => import('./pages/salaries/SalariesPage'));
const SalesReportPage = lazy(() => import('./pages/reports/SalesReportPage'));
const ProfitReportPage = lazy(() => import('./pages/reports/ProfitReportPage'));
const ShowroomSummaryPage = lazy(() => import('./pages/reports/ShowroomSummaryPage'));
const UsersPage = lazy(() => import('./pages/settings/UsersPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const BlocksPage = lazy(() => import('./pages/blocks/BlocksPage'));
const BlockFormPage = lazy(() => import('./pages/blocks/BlockFormPage'));
const BlockDetailPage = lazy(() => import('./pages/blocks/BlockDetailPage'));
const ReturnsPage = lazy(() => import('./pages/returns/ReturnsPage'));
const CreateReturnPage = lazy(() => import('./pages/returns/CreateReturnPage'));
const ReturnDetailPage = lazy(() => import('./pages/returns/ReturnDetailPage'));
const ExpensesPage = lazy(() => import('./pages/expenses/ExpensesPage'));
const AddExpensePage = lazy(() => import('./pages/expenses/AddExpensePage'));
const DeliveryChallanPage = lazy(() => import('./pages/deliveryChallan/DeliveryChallanPage'));
const CreateDeliveryChallanPage = lazy(() => import('./pages/deliveryChallan/CreateDeliveryChallanPage'));
const DeliveryChallanDetailPage = lazy(() => import('./pages/deliveryChallan/DeliveryChallanDetailPage'));

const LoadingSpinner = () => (
  <div style={{
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: '#f8fafc', zIndex: 9999
  }}>
    <div style={{ width: '50px', height: '50px', border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
    <p style={{ marginTop: '16px', color: '#64748b' }}>Loading ERP System...</p>
  </div>
);

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('erp_token');
    const user = localStorage.getItem('erp_user');
    setIsAuthenticated(!!(token && user));
    setLoading(false);
  }, []);

  return { isAuthenticated, loading };
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Router>
      <IdleProvider>
        <SettingsProvider>
          <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
                <Route path="/" element={<PrivateRoute><Navigate to="/dashboard" /></PrivateRoute>} />
                <Route path="/dashboard" element={<PrivateRoute><DashboardPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/analytics" element={<PrivateRoute><AnalyticsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/products" element={<PrivateRoute><ProductsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/products/add" element={<PrivateRoute><AddProductPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/products/edit/:id" element={<PrivateRoute><AddProductPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/products/:id" element={<PrivateRoute><ViewProductPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/blocks" element={<PrivateRoute><BlocksPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/blocks/new" element={<PrivateRoute><BlockFormPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/blocks/edit/:id" element={<PrivateRoute><BlockFormPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/blocks/:id" element={<PrivateRoute><BlockDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/stock" element={<PrivateRoute><StockPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/stock/adjust" element={<PrivateRoute><AdjustStockPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/warehouses" element={<PrivateRoute><WarehousesPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/warehouses/:id" element={<PrivateRoute><WarehouseDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/showrooms" element={<PrivateRoute><ShowroomsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/showrooms/:id" element={<PrivateRoute><ShowroomDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/invoices" element={<PrivateRoute><InvoicesPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/invoices/create" element={<PrivateRoute><CreateInvoicePage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/invoices/edit/:id" element={<PrivateRoute><CreateInvoicePage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/invoices/:id" element={<PrivateRoute><InvoiceDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/quotations" element={<PrivateRoute><QuotationsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/quotations/create" element={<PrivateRoute><CreateQuotationPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/quotations/edit/:id" element={<PrivateRoute><CreateQuotationPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/quotations/:id" element={<PrivateRoute><QuotationDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/salaries" element={<PrivateRoute><SalariesPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/reports/sales" element={<PrivateRoute><SalesReportPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/reports/profit" element={<PrivateRoute><ProfitReportPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/reports/showrooms" element={<PrivateRoute><ShowroomSummaryPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/users" element={<PrivateRoute><UsersPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><SettingsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/notifications" element={<PrivateRoute><NotificationsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/returns" element={<PrivateRoute><ReturnsPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/returns/new" element={<PrivateRoute><CreateReturnPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/returns/edit/:id" element={<PrivateRoute><CreateReturnPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/returns/:id" element={<PrivateRoute><ReturnDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/expenses" element={<PrivateRoute><ExpensesPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/expenses/add" element={<PrivateRoute><AddExpensePage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/expenses/edit/:id" element={<PrivateRoute><AddExpensePage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/delivery-challans" element={<PrivateRoute><DeliveryChallanPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/delivery-challans/create" element={<PrivateRoute><CreateDeliveryChallanPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/delivery-challans/edit/:id" element={<PrivateRoute><CreateDeliveryChallanPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
                <Route path="/delivery-challans/:id" element={<PrivateRoute><DeliveryChallanDetailPage sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} /></PrivateRoute>} />
              </Routes>
            </Suspense>
          </div>
          <IdleWarningModal />
        </SettingsProvider>
      </IdleProvider>
    </Router>
  );
}

export default App;